import * as React from "react"
import { createAvatar } from "@dicebear/core"
import * as notionists from "@dicebear/notionists"

// ═══════════════════════════════════════════════════════════════════════════
// AVATAR — ported verbatim from PayvitDashboard.tsx, not reimplemented.
//
// Same DiceBear collection (notionists), same cache keyed on
// `presentation:seed`, same gendered hair pools, same brand background ramp,
// same initials fallback. Seeded by a stable id so a person keeps the same
// face across devices and sessions, permanently — which is why the seed is
// an id and never a display name.
// ═══════════════════════════════════════════════════════════════════════════

export const AVATAR_BG = ["1A9D8B", "41C6B5", "5EEAD4", "0E7A6C", "16564F", "9ADBD0"]

const HAIR_FEMININE = [
    "variant01","variant02","variant03","variant04","variant05","variant06",
    "variant07","variant08","variant09","variant10","variant11","variant12",
    "variant13","variant14","variant15","variant16",
]
const HAIR_MASCULINE = [
    "variant21","variant22","variant23","variant24","variant25","variant26",
    "variant27","variant28","variant29","variant30","variant31","variant32",
    "variant33","variant34","variant35","variant36",
]

export type Presentation = "feminine" | "masculine" | "neutral"

/** Name → presentation. Same principle as the dashboard's: a wrong guess is
 *  worse than no guess, so anything unlisted stays neutral. */
const FEM = new Set([
    "naomie","sarah","mirlande","alina","marie","rose","nadege","darline","fabiola",
    "guerline","islande","kettya","lourdes","magalie","roseline","yolande","chantal",
])
const MASC = new Set([
    "jean","kervens","audemar","mark","pierre","jacques","wilson","frantz","emmanuel",
    "gary","herby","junior","luckner","ronald","stanley","widlen","evens",
])
export function inferPresentation(displayName: string): Presentation {
    const first = (displayName || "").trim().split(/[\s-]+/)[0]?.toLowerCase() ?? ""
    const n = first.normalize("NFD").replace(/[̀-ͯ]/g, "")
    if (FEM.has(n)) return "feminine"
    if (MASC.has(n)) return "masculine"
    return "neutral"
}

const cache = new Map<string, string | null>()
export function avatarUri(seed: string, presentation: Presentation = "neutral"): string | null {
    if (!seed) return null
    const key = `${presentation}:${seed}`
    if (cache.has(key)) return cache.get(key)!
    let uri: string | null = null
    try {
        const gendered =
            presentation === "feminine"
                ? { hair: HAIR_FEMININE, beardProbability: 0 }
                : presentation === "masculine"
                  ? { hair: HAIR_MASCULINE, beardProbability: 25 }
                  : { beardProbability: 0 }
        const svg = createAvatar(notionists, {
            seed, size: 128, radius: 50,
            backgroundColor: AVATAR_BG, backgroundType: ["solid"],
            ...gendered,
        } as any).toString()
        uri = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
    } catch (_e) {
        uri = null
    }
    cache.set(key, uri)
    return uri
}

export function Avatar({
    seed, size = 40, name, ring, presentation,
}: {
    seed: string
    size?: number
    name?: string
    ring?: boolean
    presentation?: Presentation
}) {
    const resolved = presentation ?? inferPresentation(name ?? "")
    const uri = React.useMemo(() => avatarUri(seed, resolved), [seed, resolved])
    const initial = (name?.trim()?.charAt(0) || "?").toUpperCase()
    const idx = React.useMemo(() => {
        let h = 0
        for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
        return h % AVATAR_BG.length
    }, [seed])
    return (
        <span
            className="pv-av"
            style={{
                width: size, height: size, fontSize: size * 0.38,
                background: uri ? "transparent" : `#${AVATAR_BG[idx]}`,
                boxShadow: ring
                    ? "0 0 0 3px rgba(255,255,255,.65), 0 8px 20px -10px rgba(7,40,38,.7)"
                    : "0 6px 14px -8px rgba(7,40,38,.45)",
            }}
        >
            {uri ? <img src={uri} alt="" width={size} height={size} draggable={false} /> : initial}
        </span>
    )
}
